import { useParams, useNavigate, Link as RouterLink } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import DashboardLayout from "../layouts/dashboard";
import { StaggerContainer, StaggerItem } from "../../interface/shared/animations";
import { useAuditLogEntryDetail } from "../../api/audit";
import { AuditLogEntry, AuditLogEntryLink } from "../../shared/types";

export default function AuditDetails() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { auditLogEntry, errors, loading, fetchAuditLogEntry } = useAuditLogEntryDetail(id);

    return (
        <DashboardLayout>
            <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 3 }}>
                    <Button
                        startIcon={<ArrowBackRoundedIcon />}
                        onClick={() => navigate("/audit")}
                    >
                        Back
                    </Button>

                    {auditLogEntry?.link && (() => {
                        const path = _linkPath(auditLogEntry.link!);
                        return path ? (
                            <Button
                                component={RouterLink}
                                to={path}
                                variant="outlined"
                                size="small"
                                endIcon={<OpenInNewRoundedIcon fontSize="small" />}
                            >
                                View {auditLogEntry.link!.type.replace(/[-_]/g, " ")}
                            </Button>
                        ) : null;
                    })()}
                </Stack>

                {errors.length > 0 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errors.join(" ")}
                    </Alert>
                )}

                {loading ? <_DetailsSkeleton /> : auditLogEntry && (
                    <StaggerContainer>
                        <StaggerItem>
                            <_Summary entry={auditLogEntry} />
                        </StaggerItem>

                        {_hasChanges(auditLogEntry) && (
                            <StaggerItem>
                                <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
                                    Changes
                                </Typography>
                                <_ChangesTable entry={auditLogEntry} />
                            </StaggerItem>
                        )}
                    </StaggerContainer>
                )}
            </Box>
        </DashboardLayout>
    );
}

function _hasChanges(entry: AuditLogEntry): boolean {
    const oldKeys = entry.old_values ? Object.keys(entry.old_values) : [];
    const newKeys = entry.new_values ? Object.keys(entry.new_values) : [];
    return oldKeys.length > 0 || newKeys.length > 0;
}

function _eventColor(event: string): "default" | "success" | "warning" | "error" | "info" {
    if (event.includes("created") || event.includes("added")) return "success";
    if (event.includes("updated") || event.includes("modified")) return "info";
    if (event.includes("deleted") || event.includes("removed")) return "error";
    return "default";
}

function _Summary({ entry }: { entry: AuditLogEntry }) {
    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="h6">
                            Audit Log
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {entry.display_type}
                        </Typography>
                    </Box>
                    <Chip
                        label={entry.event}
                        color={_eventColor(entry.event)}
                        size="small"
                        sx={{ textTransform: "capitalize" }}
                    />
                </Stack>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Timestamp</Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {entry.created_at.toLocaleDateString()} {entry.created_at.toLocaleTimeString()}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">User</Typography>
                        <Typography variant="body1" fontWeight={500}>{entry.user.name}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Event</Typography>
                        <Typography variant="body1" fontWeight={500} sx={{ textTransform: "capitalize" }}>
                            {entry.event}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Type</Typography>
                        <Typography variant="body1" fontWeight={500}>{entry.display_type}</Typography>
                    </Grid>
                </Grid>

                {entry.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {entry.description}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}

function _ChangesTable({ entry }: { entry: AuditLogEntry }) {
    const allKeys = new Set([
        ...Object.keys(entry.old_values ?? {}),
        ...Object.keys(entry.new_values ?? {}),
    ]);

    return (
        <Card variant="outlined">
            <CardContent>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Field</TableCell>
                            <TableCell>Old Value</TableCell>
                            <TableCell>New Value</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.from(allKeys).map((key) => {
                            const oldVal = entry.old_values?.[key];
                            const newVal = entry.new_values?.[key];
                            const changed = JSON.stringify(oldVal) !== JSON.stringify(newVal);

                            return (
                                <TableRow key={key}>
                                    <TableCell sx={{ fontWeight: 500, textTransform: "capitalize" }}>
                                        {key.replace(/_/g, " ")}
                                    </TableCell>
                                    <TableCell sx={changed ? { color: "error.main" } : undefined}>
                                        {_formatValue(oldVal)}
                                    </TableCell>
                                    <TableCell sx={changed ? { color: "success.main" } : undefined}>
                                        {_formatValue(newVal)}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}

function _formatValue(value: any): string {
    if (value === null || value === undefined) return "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
}

function _linkPath(link: AuditLogEntryLink): string | null {
    const routes: Record<string, string> = {
        product: "/products",
        stock_relocate_request: "/relocations",
        stock: "/stock",
        relocation: "/relocations",
        delivery: "/goods-in",
        "inbound-delivery": "/goods-in",
        location: "/locations",
        "stock-location": "/locations",
    };

    const base = routes[link.type];
    if (!base) return null;
    return `${base}/${link.id}`;
}

function _DetailsSkeleton() {
    return (
        <>
            <Card variant="outlined">
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 3 }}>
                        <Box>
                            <Skeleton variant="text" width={180} height={32} />
                            <Skeleton variant="text" width={120} height={20} />
                        </Box>
                        <Skeleton variant="rounded" width={80} height={24} />
                    </Stack>
                    <Grid container spacing={3}>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Grid key={i} size={{ xs: 6, sm: 3 }}>
                                <Skeleton variant="text" width={80} height={16} />
                                <Skeleton variant="text" width={120} height={24} />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
            <Skeleton variant="text" width={100} height={28} sx={{ mt: 4, mb: 2 }} />
            <Card variant="outlined">
                <CardContent>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Field</TableCell>
                                <TableCell>Old Value</TableCell>
                                <TableCell>New Value</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.from({ length: 4 }).map((_, i) => (
                                <TableRow key={i}>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
            <Skeleton variant="text" width={80} height={28} sx={{ mt: 4, mb: 2 }} />
            <Card variant="outlined">
                <CardContent>
                    <Skeleton variant="text" width="60%" height={24} />
                    <Skeleton variant="rounded" width={140} height={32} sx={{ mt: 2 }} />
                </CardContent>
            </Card>
        </>
    );
}
